import FiltersService from "@/services/FiltersService";
import { defineStore, acceptHMRUpdate } from "pinia";
import { useAuthStore } from "./auth.store";
import { i18n } from '@/main';

// Store Pinia obsługujący filtry w widoku bieżących pomiarów
export const useCurrentFiltersStore = defineStore({
  id: "currentFilter",
  state: () => ({
    // Aktualnie wybrane filtry przez użytkownika (przesyłane do backendu)
    filters: {
      orderDirection: "desc",
      overload: false,
      overheight: false,
      overspeed: false,
      overlane: false,
      wrongDirection: false,
    },
    // Lista dostępnych lokalizacji (pobierana z API)
    locations: [],
    // Lista dostępnych numerów pasów
    laneNumbers: [],
    // Dostępne kategorie pojazdów
    vehicleCategories: [2, 3, 5, 7, 8, 9, 10, 11, i18n.global.t('lack')],
    // Opis pól danych pojazdu - używane przy generowaniu filtrów
    vehicleData: {
      plateLpr: "registration",
      similarity: "similarity",
      minSpeed: "speed",
      maxSpeed: "speed",
      minTotalLoad: "short-total-weight",
      maxTotalLoad: "short-total-weight",
      minVehLength: "length",
      maxVehLength: "length",
      minVehHeight: "height",
      maxVehHeight: "height",
      minNumberOfAxles: "no-of-axles",
      maxNumberOfAxles: "no-of-axles",
      minOverloadSA: "single-axle",
      maxOverloadSA: "single-axle",
      minOverloadMA: "multiple-axle",
      maxOverloadMA: "multiple-axle",
      minOverloadTL: "dmc",
      maxOverloadTL: "dmc",
    },
    // Dostępne naruszenia (wykorzystywane m.in. do checkboxów filtrów)
    violations: {
      overload: i18n.global.t('permissible-load'),
      overheight: i18n.global.t('permissible-height'),
      overspeed: i18n.global.t('speed'),
      overlane: i18n.global.t('bypassing'),
      wrongDirection: i18n.global.t('wrong-way'),
    },
    // Kolumny sortowania dostępne w filtrze
    orderByColumns: {
      createdAt: "measure-time",
      locationId: "location",
      plateLpr: "short-plate-no",
      speed: "speed",
      totalLoad: "total-weight",
      vehLength: "length",
      vehHeight: "height",
      numberOfAxles: "no-of-axles",
      ctg8p1: "vehicle-type",
      overloadSA: "single-axle",
      overloadMA: "multiple-axle",
      overloadTL: "dmc",
    },
    // Kierunki sortowania
    directionColumns: { desc: "ascending", asc: "descending" },
    // Store do obsługi autoryzacji (dla error handlingu)
    authStore: useAuthStore(),
    // Brakujące wymagane pola (do walidacji formularza)
    requireFields: [],
    // Flaga błędu
    error: null,
  }),
  getters: {
    // Zwraca obecnie ustawione filtry
    items: (state) => state.filters,
    // Zwraca wybrane lokalizacje na podstawie id z filtrów
    selectedLocation: (state) => {
      if (state.filters.locations) {
        return state.locations.filter((location) =>
          state.filters.locations.includes(location.id)
        );
      }
      return [];
    },
  },
  actions: {
    // Dodaje/usuwa wartości filtrów na podstawie ich typu/nazwy
    addItem(name, value) {
      const handlers = {
        // Obsługa wyboru lokalizacji (checkbox/lista)
        LocationList: (value) => {
          if (!this.filters.locations) this.filters.locations = [];
          this.toggleItemInArray(this.filters.locations, value);
        },
        // Obsługa wyboru kategorii pojazdów
        VehicleCategories: (value) => {
          if (!this.filters.ctg8p1) this.filters.ctg8p1 = [];
          this.toggleItemInArray(this.filters.ctg8p1, this.vehicleCategories[value]);
        },
        // Obsługa wyboru numerów pasów
        LaneNumbers: (value) => {
          if (!this.filters.laneNumbers) this.filters.laneNumbers = [];
          this.toggleItemInArray(this.filters.laneNumbers, this.laneNumbers[value]);
        },
        // Zaznacz/odznacz wszystkie naruszenia
        ViolationsAll: () => {
          const allChecked = Object.keys(this.violations).every((key) => this.filters[key]);
          Object.keys(this.violations).forEach((key) => {
            if (allChecked) delete this.filters[key];
            else this.filters[key] = true;
          });
        }
      };
    
      const handler = handlers[name];
      if (handler) {
        handler(value);
      } else {
        this.filters[name] = value;
      }
    },

    // Uniwersalna funkcja przełączania elementów w tablicy (np. zaznaczanie/odznaczanie checkboxów)
    toggleItemInArray(array, value) {
      const index = array.indexOf(value);
      if (index === -1) {
        array.push(value);
      } else {
        array.splice(index, 1);
      }
    },

    // Pobiera lokalizacje z API i generuje na ich podstawie numery pasów
    async getLocations() {
      if (this.locations.length === 0) {
        try {
          const data = await FiltersService.getLocations();
          this.locations = data;

          const lanesSet = new Set();
          this.locations.forEach((location) => {
            const laneCount = location.lanes.length;
            for (let i = 1; i <= laneCount; i++) {
              lanesSet.add(i);
            }
          });

          this.laneNumbers = [...lanesSet].sort((a, b) => a - b);
        } catch (error) {
          console.error("Błąd pobierania lokalizacji:", error);
        }
      }
    },

    // Zaznacz/odznacz wszystkie lokalizacje
    checkAllLocations() {
      const allSelected = this.filters.locations?.length === this.locations.length;
      this.filters.locations = allSelected ? [] : this.locations.map((loc) => loc.id);
    },

    // Usuwanie elementów filtrów
    removeItem(name) {
      const removals = {
        // Usuwa zakres dat
        rangeOfDate: () => {
          delete this.filters.dateTimeStart;
          delete this.filters.dateTimeEnd;
        },
        // Usuwa wszystkie naruszenia
        violations: () => {
          Object.keys(this.violations).forEach((key) => delete this.filters[key]);
        },
        // Usuwa wszystkie filtry pojazdu
        vehicleData: () => {
          Object.keys(this.vehicleData).forEach((key) => delete this.filters[key]);
        },
        // Domyślnie usuwa pojedynczy filtr
        default: () => {
          delete this.filters[name];
        },
      };

      (removals[name] || removals.default)();
    },

    // Sprawdza, czy wymagane pola zostały uzupełnione
    checkRequireData() {
      this.requireFields = [];
      this.error = false;

      if (!this.filters.locations?.length) {
        this.requireFields.push("locations");
      }

      if (this.requireFields.length) {
        this.error = true;
        this.setErrors(this.$t("required-parameter-missing"));
      }
    },

    // Przekazuje błąd do globalnego store od autoryzacji
    setErrors(error) {
      this.authStore.errorMessage = error;
    },
  },

  // (NIEUŻYWANE w akcji - alternatywne podejście)
  filterHandlers: {
    LocationList(store, value) {
      if (!store.filters.locations) store.filters.locations = [];
      store.toggleItemInArray(store.filters.locations, value);
    },
    VehicleCategories(store, value) {
      if (!store.filters.ctg8p1) store.filters.ctg8p1 = [];
      store.toggleItemInArray(store.filters.ctg8p1, store.vehicleCategories[value]);
    },
    LaneNumbers(store, value) {
      if (!store.filters.laneNumbers) store.filters.laneNumbers = [];
      store.toggleItemInArray(store.filters.laneNumbers, store.laneNumbers[value]);
    },
    ViolationsAll(store) {
      const allChecked = Object.keys(store.violations).every((key) => store.filters[key]);
      Object.keys(store.violations).forEach((key) => {
        if (allChecked) delete store.filters[key];
        else store.filters[key] = true;
      });
    },
  },
});

// Hot module reload - do dev
if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useCurrentFiltersStore, import.meta.hot)
  );
}
